import { useState } from "react";
import { ArchiveBoxXMarkIcon, ArrowPathIcon, ArrowUturnLeftIcon, LockClosedIcon, RocketLaunchIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, ConfirmModal, Tag, Textarea } from "../ui";
import { errorMessageOf, problemMessage, problemOf, scholarshipsApi } from "../../api/scholarships";
import { formatKigaliDate, hasDestination, kindOf, needsSponsor, stateOf, useScholarshipChange } from "./model";
import { READ_ONLY_NOTE, canManageScholarships } from "./access";
import ReadinessChecklist from "../calls/ReadinessChecklist";
import ReopenDialog from "./ReopenDialog";
import EvidenceCard, { EVIDENCE } from "./EvidenceCard";

// What publishing needs, checked here for guidance; the backend's 422 publish-incomplete decides
const readiness = (s) => {
  const programmes = s.programmes ?? [];
  const everyProgramme = (ready) => programmes.length > 0 && programmes.every(ready);
  const weights = (rubric = []) => rubric.reduce((sum, c) => sum + (c.weight ?? 0), 0);
  return [
    { label: "Academic year chosen", ok: Boolean(s.academicYear) },
    ...(hasDestination(s.type) ? [{ label: "Destination country chosen", ok: Boolean(s.destinationCountryCode) }] : []),
    { label: "Description written", ok: Boolean(s.description?.trim()) },
    ...(needsSponsor(s.type) ? [{ label: "Partner chosen", ok: Boolean(s.sponsor) }] : []),
    { label: "Approval evidence uploaded", ok: Boolean(s.approvalEvidence) },
    ...(s.visibility === "PRIVATE" ? [{ label: "Reason for private visibility uploaded", ok: Boolean(s.privacyEvidence) }] : []),
    { label: "Opening and closing dates set, opening first", ok: Boolean(s.opensAt && s.closesAt && new Date(s.opensAt) < new Date(s.closesAt)) },
    { label: "At least one programme", ok: programmes.length > 0 },
    { label: "Required documents for every programme", ok: everyProgramme((p) => (p.requirements ?? []).length > 0) },
    { label: "Selection criteria adding up to 100 for every programme", ok: everyProgramme((p) => weights(p.rubric) === 100) },
  ];
};

const MIN_REASON = 10;

// The details a publish gap of code "missing" names
const DETAIL_LABELS = { description: "Description", academicYearId: "Academic year", destinationCountryCode: "Destination country", opensAt: "Opening date", closesAt: "Closing date" };

// A publish gap as a sentence; per-programme gaps (`programmes[<id>].requirements`, `.rubric`) name the programme
const gapMessage = (error, scholarship) => {
  if (error.code === "missing") return `${DETAIL_LABELS[error.field] ?? error.field}: ${problemMessage("missing")}`;
  const programmeId = /^programmes\[([^\]]+)\]/.exec(error.field ?? "")?.[1];
  const programme = programmeId && (scholarship.programmes ?? []).find((p) => p.id === programmeId);
  const message = problemMessage(error.code) ?? error.code;
  return programme ? `${programme.name}: ${message}` : message;
};

/**
 * Journey step 4 and the rest of the scholarship's lifecycle: publish, unpublish, close, reopen, cancel.
 * Only the moves the current state allows are offered.
 */
export default function StatusTab({ scholarship }) {
  const change = useScholarshipChange(scholarship.id);
  const [pending, setPending] = useState(null); // { event, title, ... }
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [gaps, setGaps] = useState([]); // from 422 publish-incomplete
  const [reopening, setReopening] = useState(false);

  const state = stateOf(scholarship.state);
  const submitted = scholarship.submittedApplications ?? 0;
  const closesEarly = new Date() < new Date(scholarship.closesAt);
  const checks = readiness(scholarship);
  const { one } = kindOf(scholarship.type);

  const actions = [
    scholarship.state === "DRAFT" && {
      event: "PUBLISH",
      label: "Publish",
      icon: RocketLaunchIcon,
      title: `Publish this ${one}?`,
      description: "Students can see it and apply from its opening date. Changes then need a reason, and the set-up locks once a student starts applying: only a later closing date can be set.",
    },
    scholarship.state === "PUBLISHED" && {
      event: "UNPUBLISH",
      label: "Unpublish",
      icon: ArrowUturnLeftIcon,
      variant: "outline",
      disabledReason: submitted > 0 ? "Not possible once students have applied" : null,
      title: "Move back to draft?",
      description: "It disappears from the website and can be edited again.",
    },
    scholarship.state === "PUBLISHED" && {
      event: "CLOSE",
      label: "Close applications",
      icon: ArchiveBoxXMarkIcon,
      variant: "outline",
      needsReason: closesEarly,
      title: closesEarly ? "Close before the deadline?" : "Close applications?",
      description: closesEarly
        ? `Applications are open until ${formatKigaliDate(scholarship.closesAt)}. Unsubmitted drafts become "not submitted".`
        : 'Unsubmitted drafts become "not submitted".',
    },
    // Reopening has its own dialog: a reason, the visibility and a new closing date
    scholarship.state === "CLOSED" && {
      event: "REOPEN",
      label: "Reopen",
      icon: ArrowPathIcon,
      variant: "outline",
      dialog: true,
    },
    ["DRAFT", "PUBLISHED"].includes(scholarship.state) && {
      event: "CANCEL",
      label: `Cancel ${one}`,
      icon: XCircleIcon,
      variant: "danger",
      needsReason: true,
      disabledReason: submitted > 0 ? "Not possible once applications are submitted" : null,
      title: `Cancel this ${one}?`,
      description: "This cannot be undone.",
    },
  ].filter((a) => Boolean(a) && canManageScholarships());

  const ask = (action) => {
    if (action.dialog) return setReopening(true);
    setReason("");
    setPending(action);
  };

  const confirm = async () => {
    if (pending.needsReason && reason.trim().length < MIN_REASON) return;
    setBusy(true);
    try {
      const body = { event: pending.event, ...(pending.needsReason && { reason: reason.trim() }) };
      await change((etag) => scholarshipsApi.post(`admin/scholarships/${scholarship.id}/transitions`, body, { ifMatch: etag }));
      setGaps([]);
      toast.success(`${pending.label}: done`);
      setPending(null);
    } catch (error) {
      console.error(`Failed to ${pending.event} scholarship ${scholarship.id}:`, error);
      const problem = problemOf(error);
      if (problem?.code === "publish-incomplete") {
        setGaps(problem.errors.map((e) => gapMessage(e, scholarship)));
        setPending(null);
      }
      toast.error(errorMessageOf(error, "That did not work. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-slate-600">Current state</span>
        <Tag color={state.color}>{state.label}</Tag>
        <span className="text-sm text-slate-500">
          {submitted} submitted application{submitted === 1 ? "" : "s"}
        </span>
      </div>

      <EvidenceCard scholarship={scholarship} kind={EVIDENCE.approval} />

      {scholarship.state === "DRAFT" && <ReadinessChecklist checks={checks} />}

      {gaps.length > 0 && (
        <section className="rounded-lg bg-red-50 p-4">
          <h3 className="mb-2 text-sm font-semibold text-red-800">Publishing needs these first:</h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-red-700">
            {gaps.map((gap) => (
              <li key={gap}>{gap}</li>
            ))}
          </ul>
        </section>
      )}

      {actions.length ? (
        <div className="flex flex-wrap gap-3 border-t border-slate-100 pt-5">
          {actions.map((a) => (
            <div key={a.event} className="flex flex-col gap-1">
              <Button variant={a.variant} icon={<a.icon className="h-4 w-4" />} onClick={() => ask(a)} disabled={Boolean(a.disabledReason)}>
                {a.label}
              </Button>
              {a.disabledReason && <span className="text-xs text-slate-500">{a.disabledReason}</span>}
            </div>
          ))}
        </div>
      ) : (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <LockClosedIcon className="h-4 w-4" />
          {canManageScholarships() ? "No further changes are possible in this state." : READ_ONLY_NOTE}
        </p>
      )}

      <ReopenDialog scholarship={scholarship} open={reopening} onClose={() => setReopening(false)} />

      <ConfirmModal
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        onConfirm={confirm}
        loading={busy}
        destructive={pending?.event === "CANCEL"}
        title={pending?.title}
        description={pending?.description}
        confirmLabel={pending?.label}
      >
        {pending?.needsReason && (
          <Textarea
            label="Reason"
            required
            rows={3}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={reason && reason.trim().length < MIN_REASON ? `At least ${MIN_REASON} characters` : undefined}
          />
        )}
      </ConfirmModal>
    </div>
  );
}
